/* "Little surprises" (owner request 2026-10-13). Small, quiet extras; one Settings switch (per device, localStorage
   'ot_surprises' = '0' when Off; On by default) turns every one of them off:
   - holiday doodles on the header logo (lib/holidays.ts), a toast when the logo is tapped, and the day's ≤3 s effect: on
     every load, on a logo tap, on Refresh (the ↻ button or pulling down) and now and then (every 45-120 minutes, only at a quiet moment; nothing stored);
   - the logo tapped 7 times: a card with the version and the person's OWN hours (owner / admin also: entries kept);
   - a sparkle when Magic Time Sync lines up (once a day); milestone cheers (own hours); "Perfect hour" at exactly 1:00:00;
   - a night owl beside RUNNING while a timer runs outside the Timeline's 6 AM-10 PM strip (a sleepy one 1-4:59 AM, the
     person's own zone); the full-screen flip clock;
   - the keyboard code (↑↑↓↓←→←→BA) on a computer: 10 s of rainbow colours (never purple);
   - the owner's daily line at the bottom of Settings;
   - Advent (lib/advent.ts): the calendar door and its 24 doors, the wreath, the countdown, growing snow, St Nicholas,
     the Christmas Eve sleigh, the Christmas Day star;
   - the Easter egg hunt, Good Friday to Easter Monday (eggs.ts, lib/eggs.ts);
   - birthdays (birthdays.ts, lib/birthdays.ts, bdayfx.ts): the person's own (🎂, the card, the big party) and family &
     friends' (🎈, a few balloons), from their own list (nobody else's), on their own day;
   - "Preview a day" (the owner only, in the 7-tap card; lib/preview.ts): the surprises see another date (the real time
     of day) until Stop or the owner's midnight. Only the surprises read it (surpriseWall below): never entries, timers,
     totals, Reports or sync. Eggs found / doors opened meanwhile go to a preview-only store, cleared on Stop.
   Rules: nothing here changes, creates or deletes a record, sends anything new anywhere, or shows anyone else's data;
   nothing blocks a button or the app's own updates (nothing here counts as an open window); every effect is short, uses
   transforms only, and with "reduce motion" shows a still version or nothing. The old product is never named. */
import {State, me} from './state.ts';
import {can} from './lib/model.ts';
import type {Timer} from './lib/model.ts';
import {fmtN} from './lib/time.ts';
import * as db from './db.ts';
import {myTz, myWall} from './zone.ts';
import {escapeHtml, toast} from './ui/common.ts';
import {versionText} from './updater.ts';
import {holidayOn, type Holiday, type Fx} from './lib/holidays.ts';
import {tapCounter, konamiDetector, milestoneStep, milestoneText, isPerfectHour, PERFECT_HOUR, owlAt, OWL_LABEL, SLEEPY_LABEL, type OwlKind, ownerLine, fxGap, FX_RETRY, replayGate, quietMoment} from './lib/surpriseLogic.ts';
import {parsePreview, previewOn, previewWall, previewLabel, validDate, PREVIEW_KEY, PREVIEW_STORE, PREVIEW_MIN, PREVIEW_MAX, type Preview} from './lib/preview.ts';
import {birthdaysOn, othersText, ownText, firstName, logoPlan, tapMessage} from './lib/birthdays.ts';
import {myList, listVersion, birthdaysCard, wireBirthdaysCard, refreshCard, hooks as bdHooks} from './birthdays.ts';
import {birthdayFx, fxPlaying as bdFxPlaying, openBirthdayCard, birthdayCardOpen} from './bdayfx.ts';
import {wreathCandles, countdown, adventDay, doorOpenable, calendarDays, isChristmasDay, isStNicholas, snowFlakes, sleighTime, DOORS, doorText} from './lib/advent.ts';

/* ---------- the switch, and small storage helpers ---------- */
const ON_KEY = 'ot_surprises';
const get = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const put = (k: string, v: string | null) => { try { v === null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch { /* private mode */ } };
/** are the little surprises on, on this device? (default On) */
export const surprisesOn = () => get(ON_KEY) !== '0';
export function setSurprisesOn(on: boolean) {
  put(ON_KEY, on ? null : '0');
  if (!on) { closeCard(); document.querySelectorAll('.sx-fx, .sx-sleigh, .sx-burst, .sx-rainbow, .sx-egg, .sx-bd-fx, .sx-bd-cannon').forEach(e => e.remove()); stopDisco(false); }
  lastKey = ''; refreshSurprises(); tickOwls();
}
export const reduced = () => { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; } };

/* ---------- "Preview a day" (the owner only; lib/preview.ts): the date the Little surprises see ---------- */
const realWall = () => myWall(Date.now());
/** the owner's preview, when it is on now (it ends by itself on his next real day: then it is cleared) */
function activePreview(): Preview | null {
  const p = parsePreview(get(PREVIEW_KEY)), real = realWall().slice(0, 10);
  if (p && previewOn(p, State.role, State.meId, real)) return p;
  if (p && p.setOn !== real) clearPreview();   // (another person signed in on this device: kept, just not used)
  else if (!p && get(PREVIEW_KEY) !== null) clearPreview();   // (something odd saved: gone)
  return null;
}
function clearPreview() {
  put(PREVIEW_KEY, null);
  try { Object.keys(localStorage).filter(k => k.startsWith(PREVIEW_STORE)).forEach(k => localStorage.removeItem(k)); } catch { /* private mode */ }
}
/** the moment ('YYYY-MM-DDTHH:MM:SS', the person's own zone) the Little surprises see: the real one, or during the owner's
    preview the previewed date with the real time of day. ONLY the surprises (this file, eggs.ts) may use it. */
export const surpriseWall = (ms = Date.now()) => { const p = activePreview(); return previewWall(myWall(ms), p ? p.date : null); };
export const surpriseDay = () => surpriseWall().slice(0, 10);
/** where found eggs / opened Advent doors are kept: their real key, or during a preview a preview-only one */
export const surpriseStoreKey = (k: string) => activePreview() ? PREVIEW_STORE + k : k;
const today = surpriseDay;
/** a field the person types in (a text box, a list to pick from; not a checkbox or a button) */
export const editable = (el: Element | null) => !!el && ((el as HTMLElement).isContentEditable || /^(TEXTAREA|SELECT)$/.test(el.tagName)
  || el.tagName === 'INPUT' && !/^(checkbox|radio|button|submit|reset|file|range|color)$/.test((el as HTMLInputElement).type));
/** a window, a pick-list, a card, the welcome or the full-screen timer is open */
export const windowOpen = () => !!document.querySelector('#modalRoot .modal-backdrop, .combo-panel, .sug:not([hidden]), #wlCard, #wlGuide, #sxCard');
export const fullScreen = () => !!document.querySelector('#fsRoot .fs-overlay');

/* ---------- small drawings (inline SVG; they look the same in light and dark) ---------- */
const PUMPKIN = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 7.2c-.1-2 .6-3.4 2-4.3" fill="none" stroke="#4e7a26" stroke-width="1.8" stroke-linecap="round"/>
  <path d="M13.4 5.2c1.8-1.6 4.3-1.4 5.4.1-1.9.9-3.8.8-5.4-.1z" fill="#6aa336"/>
  <ellipse cx="7.4" cy="14.6" rx="5.2" ry="6.6" fill="#d9701a"/><ellipse cx="16.6" cy="14.6" rx="5.2" ry="6.6" fill="#d9701a"/>
  <ellipse cx="12" cy="14.6" rx="4.9" ry="7.1" fill="#f39a3d"/>
  <path d="M9.7 8.6c-1 3.6-1 8.6 0 12M14.3 8.6c1 3.6 1 8.6 0 12" fill="none" stroke="#c45f12" stroke-width=".8" opacity=".75"/></svg>`;
const FLAG = (() => { let s = ''; for (let i = 0; i < 7; i++) s += `<rect x="0" y="${(i * 2 * 16 / 13).toFixed(2)}" width="24" height="${(16 / 13).toFixed(2)}" fill="#b22234"/>`;
  return `<svg viewBox="0 0 24 16" aria-hidden="true"><rect width="24" height="16" fill="#fff"/>${s}<rect width="10.4" height="8.6" fill="#3c3b6e"/>
    <g fill="#fff">${[[2, 2], [5, 2], [8, 2], [3.5, 4.3], [6.5, 4.3], [2, 6.6], [5, 6.6], [8, 6.6]].map(([x, y]) => `<circle cx="${x}" cy="${y}" r=".6"/>`).join('')}</g>
    <rect width="24" height="16" fill="none" stroke="rgba(0,0,0,.18)" stroke-width=".5"/></svg>`; })();
const GROUNDHOG = `<svg viewBox="0 0 24 24" aria-hidden="true"><ellipse cx="12" cy="21" rx="9" ry="2.6" fill="#5b4330"/>
  <path d="M6 21c0-6 2.4-11 6-11s6 5 6 11z" fill="#9a6b43"/><circle cx="8.6" cy="9.6" r="1.6" fill="#7d5434"/><circle cx="15.4" cy="9.6" r="1.6" fill="#7d5434"/>
  <ellipse cx="12" cy="13.2" rx="4.4" ry="3.9" fill="#b07d50"/><circle cx="10.3" cy="12.4" r=".75" fill="#1d140c"/><circle cx="13.7" cy="12.4" r=".75" fill="#1d140c"/>
  <ellipse cx="12" cy="14.4" rx="1" ry=".7" fill="#3a2516"/><rect x="11.3" y="15.3" width="1.4" height="1.3" rx=".3" fill="#fff"/></svg>`;
const STOCKING = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.5 5.5h7.5v8.2c0 2.6-1.6 4.7-4.4 5.9l-3.5 1.5c-1.8.8-3.9-.1-4.4-2-.5-1.7.4-3.3 2-4l2.8-1.2z" fill="#d43a3a"/>
  <rect x="7.6" y="2.6" width="9.4" height="3.8" rx="1.3" fill="#fff" stroke="#e7dacb" stroke-width=".5"/><circle cx="6.3" cy="18" r="1.1" fill="#fff" opacity=".85"/></svg>`;
/** the Advent wreath, drawn around the logo: green ring, red berries, four candles, `lit` of them burning */
function wreathSvg(lit: number): string {
  const leaves = Array.from({length: 14}, (_, i) => { const a = i / 14 * Math.PI * 2, x = 22 + Math.cos(a) * 17, y = 25 + Math.sin(a) * 17;
    return `<ellipse cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" rx="4.2" ry="2.4" transform="rotate(${(a * 180 / Math.PI + 90).toFixed(0)} ${x.toFixed(1)} ${y.toFixed(1)})" fill="${i % 2 ? '#2f7d3a' : '#3f9a48'}"/>`; }).join('');
  const berries = [30, 110, 200, 290].map(d => { const a = d * Math.PI / 180; return `<circle cx="${(22 + Math.cos(a) * 18.5).toFixed(1)}" cy="${(25 + Math.sin(a) * 18.5).toFixed(1)}" r="1.7" fill="#d43a3a"/>`; }).join('');
  const candles = [-130, -100, -80, -50].map((d, i) => { const a = d * Math.PI / 180, x = 22 + Math.cos(a) * 17, y = 25 + Math.sin(a) * 17;
    return `<rect x="${(x - 1.4).toFixed(1)}" y="${(y - 7).toFixed(1)}" width="2.8" height="7" rx=".6" fill="${i === 2 ? '#f2c6d6' : '#a32b4a'}"/>` +
      (i < lit ? `<path class="sx-flame" d="M${x.toFixed(1)} ${(y - 11.5).toFixed(1)}c1.4 1.6 1.4 2.8 0 3.6c-1.4-.8-1.4-2 0-3.6z" fill="#ffb238"/>` : ''); }).join('');
  return `<svg viewBox="0 0 44 46" aria-hidden="true">${leaves}${berries}${candles}</svg>`;
}
const SLEIGH = `<svg viewBox="0 0 120 34" aria-hidden="true"><g fill="#8a5a33">${[0, 26].map(x => `<g transform="translate(${x} 0)"><ellipse cx="14" cy="17" rx="8" ry="4"/><path d="M20 14l4-6M22 14l3-3M9 20l-2 7M18 20l1 7" stroke="#8a5a33" stroke-width="1.4" fill="none"/><circle cx="23" cy="10" r="2.6"/><path d="M23 7l-2-5M24 7l3-4" stroke="#6b4325" stroke-width="1" fill="none"/></g>`).join('')}
  <circle cx="51.5" cy="10" r="1.1" fill="#e5484d"/></g><path d="M30 15h30M4 15h4" stroke="#c9a46a" stroke-width=".8"/>
  <path d="M62 10h30c4 0 6 3 6 7v4H66c-3 0-4-2-4-4z" fill="#c4302b"/><path d="M60 26h40c3 0 5-2 5-4" stroke="#d6b33e" stroke-width="2" fill="none" stroke-linecap="round"/>
  <rect x="78" y="3" width="11" height="8" rx="1.5" fill="#3f9a48"/><rect x="82.6" y="3" width="1.8" height="8" fill="#d6b33e"/></svg>`;

/* ---------- the header: doodle, wreath, countdown, calendar door ---------- */
let lastKey = '';
let todaysHoliday: Holiday | null = null;
let todaysBd: {own: boolean; others: string[]} = {own: false, others: []};
const $id = (id: string) => document.getElementById(id);
const decoHtml = (h: Holiday | null, date: string) => !h ? (isStNicholas(date) ? STOCKING : '')
  : 'svg' in h.deco ? (h.deco.svg === 'pumpkin' ? PUMPKIN : h.deco.svg === 'flag' ? FLAG : GROUNDHOG) : `<span class="sx-emoji">${h.deco.emoji}</span>`;
function paintHeader(on: boolean, date: string, tz: string, preview: boolean) {
  paintPreviewPill(on && preview ? date : '');
  const deco = $id('sxDeco'), wreath = $id('sxWreath'), count = $id('sxCount'), door = $id('sxDoor'), brand = $id('brand');
  if (!deco || !wreath || !count || !door || !brand) return;
  todaysHoliday = on ? holidayOn(date, tz) : null;
  // birthdays (the person's own list): their own wins the logo spot (🎂) over a holiday; family & friends' add a 🎈
  // beside the holiday's decoration (or take the spot on an ordinary day)
  todaysBd = on ? birthdaysOn(date, myList()) : {own: false, others: []};
  const hd = on ? decoHtml(todaysHoliday, date) : '', others = todaysBd.others.length > 0, plan = logoPlan(!!hd, todaysBd);
  const d = plan.spot === 'cake' ? '<span class="sx-emoji">🎂</span>' : plan.spot === 'balloon' ? '<span class="sx-emoji">🎈</span>' : hd;
  deco.innerHTML = d; deco.hidden = !d;
  deco.dataset.holiday = todaysHoliday?.id || (on && isStNicholas(date) ? 'stnicholas' : '');
  deco.dataset.birthday = todaysBd.own ? 'own' : others ? 'others' : '';
  deco.className = 'sx-deco' + (!todaysBd.own && todaysHoliday && 'svg' in todaysHoliday.deco && todaysHoliday.deco.svg === 'flag' ? ' flag' : '');
  const bb = $id('sxBalloon');
  if (bb) bb.hidden = !plan.extraBalloon;   // (a second decoration: the 🎈 at the logo's top corner)
  const bdTitle = todaysBd.own ? 'Your birthday' : others ? othersText(todaysBd.others).replace(/^🎈 /, '') : '';
  brand.title = [todaysHoliday ? todaysHoliday.name : on && isStNicholas(date) ? 'St. Nicholas Day' : '', bdTitle].filter(Boolean).join(' · ');
  const pill = $id('sxBdayPill'); if (pill) pill.hidden = !todaysBd.own;
  const lit = on ? wreathCandles(date) : 0;
  wreath.innerHTML = lit ? wreathSvg(lit) : ''; wreath.hidden = !lit; wreath.dataset.candles = String(lit);
  brand.classList.toggle('sx-has-wreath', !!lit);
  const c = on ? countdown(date) : '';
  count.textContent = c; count.hidden = !c;
  const cal = on && calendarDays(date), row = $id('sxAdvent');
  if (row) row.hidden = !cal && !todaysBd.own;   // the door and the countdown (Dec 1-25), the 🎂 Birthday! pill: a slim line under the logo
  door.hidden = !cal;
  if (cal) {
    const n = isChristmasDay(date) ? 25 : adventDay(date), opened = openedDoors(+date.slice(0, 4)).includes(n);
    door.innerHTML = n === 25 ? '<span aria-hidden="true">⭐</span>' : `<span aria-hidden="true">🎁</span><b>${n}</b>`;
    door.classList.toggle('opened', opened);
    door.setAttribute('aria-label', n === 25 ? 'Merry Christmas! Open the star' : `Advent calendar: open door ${n}`);
    door.title = n === 25 ? 'Merry Christmas!' : `Advent calendar · door ${n}`;
  }
}

/** after every redraw (main.ts) and once a minute: keep the header in step with the person's day; play today's effects */
export function refreshSurprises() {
  const preview = !!activePreview(), wall = surpriseWall(), date = wall.slice(0, 10);
  const on = surprisesOn() && realWall() >= '2020', tz = myTz();   // a device clock that is clearly wrong (e.g. reset to 1970): no surprises
  const key = [on, tz, date, State.meId, preview, listVersion()].join('|');
  if (key !== lastKey) { lastKey = key; paintHeader(on, date, tz, preview); }
  if (!on || document.visibilityState !== 'visible' || holdFx) return;
  // every load of the app plays the day's effect once (kept in memory only: a reload plays it again; a redraw does not)
  if (fxShownFor !== date) { fxShownFor = date; const f = todaysFx(date); if (f) playFx(f.fx, f.n); }
  // a birthday's opening (also when the list arrives later, e.g. the first start on a new device): once per load
  const bdKind = todaysBd.own ? 'own' : todaysBd.others.length ? 'others' : '';
  if (bdKind && bdShownFor !== date + bdKind) { bdShownFor = date + bdKind; openingBirthday(date); }
  if (sleighTime(wall) && sleighShownFor !== date) { sleighShownFor = date; sleigh(); }
}

/* ---------- the day's effect (≤ 3 s; nothing with reduced motion): on every load, on a logo tap, and now and then ---------- */
let fxShownFor = '', sleighShownFor = '', bdShownFor = '';   // the day whose effect / sleigh / birthday this page load has shown
/* Offline starts (owner request 2026-11-01): every surprise runs from this device's copy (the person, their role, their
   time zone, the switch, preview, eggs found, doors opened: all kept on the device; drawings inline, the logo in the saved
   app). The opening effect waits until the person is known (main.ts: the device copy is on screen, the login screen, or
   no access), so it plays for their own day (zone, the owner's preview) and only once; never longer than 2 s. */
let holdFx = true;
/** the person (or nobody: the login screen) is known: the opening effect may play */
export function surprisesReady() { if (!holdFx) return; holdFx = false; clearTimeout(holdTimer); refreshSurprises(); }
let holdTimer: ReturnType<typeof setTimeout> | undefined;
/** the day's effect: the holiday's own, or December's snow that grows each day to Christmas (null: none today) */
function todaysFx(date: string): {fx: Fx; n: number} | null {
  const h = todaysHoliday, flakes = snowFlakes(date);
  let fx: Fx = h ? h.fx : 'none', n = 14;
  if (flakes && (fx === 'none' || fx === 'snow')) { fx = 'snow'; n = flakes; }
  return fx === 'none' ? null : {fx, n};
}
const fxPlaying = () => !!document.querySelector('.sx-fx, .sx-sleigh') || bdFxPlaying();
/** play the day's effect, and on Christmas Eve evening the sleigh; false when the day has neither */
function playToday(wall: string): boolean {
  const f = todaysFx(wall.slice(0, 10)), s = sleighTime(wall), b = todaysBd.own || todaysBd.others.length > 0;
  if (f) playFx(f.fx, f.n);
  if (s) sleigh();
  if (b) birthdayFx(todaysBd.own);   // (replays: the balloons and confetti, never the card)
  return !!f || s || b;
}
/** now and then (every 45-120 minutes) while the app is open: only when the page is in view and nothing is open or typed */
let randomTimer: ReturnType<typeof setTimeout> | undefined;
function scheduleRandomFx(ms = fxGap()) { clearTimeout(randomTimer); randomTimer = setTimeout(randomFx, ms); }
function randomFx() {
  const wall = surpriseWall(), date = wall.slice(0, 10);
  if (!surprisesOn() || realWall() < '2020' || !todaysFx(date) && !sleighTime(wall) && !todaysBd.own && !todaysBd.others.length) return scheduleRandomFx();
  if (!quietMoment({visible: document.visibilityState === 'visible', windowOpen: windowOpen(), typing: editable(document.activeElement), fullScreen: fullScreen()})
    || fxPlaying()) return scheduleRandomFx(FX_RETRY);   // a busy moment: look again in a couple of minutes
  playToday(wall);
  scheduleRandomFx();
}
const FX_CHARS: Record<string, string[]> = {leaves: ['🍂', '🍁', '🍂'], snow: ['❄', '❅', '❆'], petals: ['🌸', '🌼'], stars: ['★', '✦', '★'], confetti: ['', '', '']};
const COLORS: Record<string, string[]> = {stars: ['#d43a3a', '#4f9dde', '#e9e4dc'], confetti: ['#f39a3d', '#e8607a', '#3fbf9b', '#4f9dde', '#d6b33e', '#e5484d']};
export function playFx(fx: Fx, n = 14) {
  if (fx === 'none' || reduced()) return;
  const box = document.createElement('div'); box.className = 'sx-fx sx-' + fx; box.setAttribute('aria-hidden', 'true');
  const chars = FX_CHARS[fx] || ['✦'], cols = COLORS[fx];
  let html = '';
  for (let i = 0; i < n; i++) {
    const dur = 1.5 + Math.random() * 0.5, delay = Math.random() * 0.9, size = 12 + Math.random() * 10;
    html += `<i style="left:${(Math.random() * 100).toFixed(1)}%; --dur:${dur.toFixed(2)}s; --d:${delay.toFixed(2)}s; --x:${(Math.random() * 80 - 40).toFixed(0)}px; --r:${(Math.random() * 540 - 270).toFixed(0)}deg; font-size:${size.toFixed(0)}px;${cols ? ` color:${cols[i % cols.length]}; --c:${cols[i % cols.length]};` : ''}">${chars[i % chars.length]}</i>`;
  }
  box.innerHTML = html;
  document.body.appendChild(box);
  setTimeout(() => box.remove(), 3000);
}

/* ---------- Christmas Eve evening: a sleigh across the header (same rule as the effect) ---------- */
function sleigh() {
  const s = document.createElement('div'); s.className = 'sx-sleigh' + (reduced() ? ' still' : ''); s.setAttribute('aria-hidden', 'true'); s.innerHTML = SLEIGH;
  document.body.appendChild(s);
  setTimeout(() => s.remove(), reduced() ? 4000 : 3100);
}

/* ---------- taps on the logo: the holiday's toast; 7 taps within 3 s: the "about" card ---------- */
const sevenTaps = tapCounter(7, 3000), tapReplay = replayGate(6000);
function onBrandTap() {
  if (!surprisesOn()) return;
  const now = Date.now();
  if (sevenTaps(now)) { openAbout(); return; }
  // the greeting: the person's own birthday first, then the holiday's, then family & friends' birthdays
  const say = tapMessage(todaysBd.own, firstName(me()?.name), todaysHoliday ? todaysHoliday.toast : isStNicholas(today()) ? 'Happy St. Nicholas Day 🍬' : '', todaysBd.others);
  if (say) toast(say, say.includes(' · ') ? 4500 : 2600);
  if (tapReplay(now, fxPlaying())) playToday(surpriseWall(now));   // the day's effect again (not while one is still playing)
}
/** the person's own Refresh (the header's ↻ Refresh, or pulling the page down; owner 2026-10-31): the day's effect again,
    by the logo tap's rules (Off: nothing; reduce motion: no effect; at most once every 6 s, shared with the logo; never
    on top of one still playing). The effect never takes a tap (pointer-events: none); the refresh itself goes on as always. */
export function replayOnRefresh() {
  if (!surprisesOn()) return;
  const now = Date.now();
  if (realWall() < '2020') return;   // a device clock that is clearly wrong: no surprises
  if (tapReplay(now, fxPlaying())) playToday(surpriseWall(now));
}

/* ---------- a card above everything (not a window: it never holds up the app's own updates); a tap closes it ---------- */
export function closeCard() { $id('sxCard')?.remove(); }
export function openCard(inner: string, cls: string, closeOnAnyTap: boolean): HTMLElement {
  closeCard();
  const bg = document.createElement('div'); bg.id = 'sxCard'; bg.className = 'sx-card-bg';
  bg.innerHTML = `<div class="sx-card ${cls}" role="dialog" aria-modal="false">${inner}</div>`;
  bg.addEventListener('click', e => { if (closeOnAnyTap || e.target === bg || (e.target as HTMLElement).closest('[data-sx-close]')) closeCard(); });
  document.body.appendChild(bg);
  return bg;
}
const hoursText = (sec: number) => (sec / 3600).toLocaleString('en-US', {maximumFractionDigits: 1});
/** the person's own all-time hours, from the database's own report (it only ever counts what this login may read) */
async function ownSeconds(): Promise<number | null> {
  const meId = State.meId; if (!meId) return null;
  try {
    const r = await db.report({from: '', to: '', members: [meId], clients: [], projects: [], tasks: [], tags: [], billable: 'all', minHours: '', q: ''}, [], true);
    return State.meId === meId ? Number(r.total?.sec || 0) : null;
  } catch { return null; }
}
function openAbout() {
  const staff = can(State.role, 'editAll');   // owner and admins only: never the bookkeeper or members
  openCard(`<img src="/icons/favicon.svg" class="sx-about-ic" alt="" width="52" height="52">
    <h3>October Time Tracker</h3><p class="sx-built">built Oct 2026</p>
    <p class="sx-ver" id="sxVer">${escapeHtml(versionText())}</p>
    <p class="sx-hours" id="sxHours">${State.meId ? 'Your time, all time: …' : ''}</p>
    ${staff ? '<p class="sx-kept" id="sxKept">…</p>' : ''}
    ${State.role === 'owner' ? '<button type="button" class="sx-prev-link" id="sxPrevLink">Preview a day</button>' : ''}
    <p class="sx-tap">Tap anywhere to close</p>`, 'sx-about', true);
  const link = $id('sxPrevLink');   // the owner only (never an admin, the bookkeeper or a member)
  if (link) link.onclick = ev => { ev.stopPropagation(); openPreviewCard(); };
  if (State.meId) void ownSeconds().then(sec => {
    const el = $id('sxHours'); if (!el) return;
    el.innerHTML = sec === null ? 'Your hours could not be loaded right now.' : `Your time, all time: <b>${hoursText(sec)} hours</b>`;
    if (sec !== null) noteMilestone(sec);
  });
  if (staff) void db.countEntries().then(n => { const el = $id('sxKept'); if (el) el.textContent = `${fmtN(n)} entries kept safe in one place`; })
    .catch(() => { $id('sxKept')?.remove(); });
}

/* ---------- milestone cheers (own hours; per person, per device; the first check notes the level silently) ---------- */
const MS_KEY = (id: string) => 'ot_sx_ms_' + id, MS_AT = (id: string) => 'ot_sx_ms_at_' + id;
function noteMilestone(sec: number) {
  const id = State.meId; if (!id || !surprisesOn()) return;
  const raw = get(MS_KEY(id)), stored = raw === null ? null : Number(raw) || 0;
  const step = milestoneStep(stored, sec / 3600);
  put(MS_KEY(id), String(step.store)); put(MS_AT(id), String(Date.now()));
  if (step.cheer) toast(milestoneText(step.cheer), 6000);
}
/** asks the database for the person's own total at most every 6 hours (or `force`: after they stopped a timer), never
    while changes wait to be sent */
export function checkMilestones(force = false, pending = 0) {
  const id = State.meId; if (!id || !surprisesOn() || !navigator.onLine || pending) return;
  if (!force && Date.now() - Number(get(MS_AT(id)) || 0) < 6 * 3600 * 1000) return;
  void ownSeconds().then(sec => { if (sec !== null) noteMilestone(sec); });
}

/** after the Timer card stopped a timer (entries.ts): a perfect hour gets a toast after the usual one; the person's own
    stop looks for a milestone a little later (once the entry has reached the server) */
export function afterStop(durationSeconds: number, memberId: string, pending: () => number) {
  if (!surprisesOn()) return;
  if (isPerfectHour(durationSeconds)) setTimeout(() => toast(PERFECT_HOUR, 4000), 1600);
  if (memberId === State.meId) setTimeout(() => checkMilestones(true, pending()), 8000);
}

/* ---------- Magic Time Sync: a tiny sparkle from the ✨ button when it lines up (once a day per device) ---------- */
const SPARKLE_KEY = 'ot_sx_sparkle';
export function sparkle(el: HTMLElement | null) {
  const day = realWall().slice(0, 10);   // (its once-a-day mark: the real day)
  if (!el || !surprisesOn() || reduced() || get(SPARKLE_KEY) === day) return;
  put(SPARKLE_KEY, day);
  burst(el, 10, ['✦', '✧', '✨'], 'sx-burst');
}
export function burst(el: HTMLElement, n: number, chars: string[], cls: string, center = false) {
  const r = el.getBoundingClientRect(), b = document.createElement('div');
  b.className = cls; b.setAttribute('aria-hidden', 'true');
  b.style.left = (r.left + (center ? r.width / 2 : Math.min(r.width / 2, 40))) + 'px';   // (the ✨ sits at the left of its button)
  b.style.top = (r.top + r.height / 2) + 'px';
  b.innerHTML = Array.from({length: n}, (_, i) => { const a = i / n * Math.PI * 2, dist = 26 + (i % 3) * 10;
    return `<i style="--tx:${(Math.cos(a) * dist).toFixed(0)}px; --ty:${(Math.sin(a) * dist).toFixed(0)}px">${chars[i % chars.length]}</i>`; }).join('');
  document.body.appendChild(b);
  setTimeout(() => b.remove(), 1000);
}

/* ---------- the night owl (owner request 2026-11-02): while a timer runs and it is now outside the Timeline's day strip
   (6 AM-10 PM, the person's own zone: lib/time.ts), an owl beside RUNNING; 1:00-4:59 AM a sleepy one. The slot is always
   there while a timer runs (empty by day); tickOwls (every second, entries.ts) swaps what is in it at 10 PM, 1 AM, 5 AM, 6 AM. */
/* a sleepy owl: half-closed droopy eyes, a blue nightcap, a little "z" that drifts up (still with reduce motion) */
const SLEEPY_OWL = `<svg viewBox="3.5 1 22 21.5" aria-hidden="true">
  <path d="M5 21c-1.6-3-1.8-8 .6-11.4C7.4 7 9.7 6 12 6s4.6 1 6.4 3.6C20.8 13 20.6 18 19 21c-2 1.6-12 1.6-14 0z" fill="#8a5a3c"/>
  <path d="M8.2 20.6c-.8-2.4-.6-5.4 1-7.2h5.6c1.6 1.8 1.8 4.8 1 7.2-1.8.8-5.8.8-7.6 0z" fill="#e3c39d"/>
  <circle cx="9.1" cy="12" r="3" fill="#f3e3c8"/><circle cx="14.9" cy="12" r="3" fill="#f3e3c8"/>
  <path d="M6.6 11.6h5M12.4 11.6h5" stroke="#5b3a26" stroke-width="1.2" stroke-linecap="round"/>
  <path d="M7.6 12.2a1.6 1.4 0 0 0 3 0zM13.4 12.2a1.6 1.4 0 0 0 3 0z" fill="#2b1d14"/>
  <path d="M11.2 14.2h1.6L12 15.6z" fill="#f39a3d"/>
  <path d="M5.4 8.6C6.6 4.6 9.8 2.6 13.4 2.8c3.4.2 6.6 2.6 8.4 7.6.2.6-.6 1-1 .5-1-1.6-2-2.6-3.2-3.2-2.4.6-8.6 1-12.2 1z" fill="#3d6fb5"/>
  <path d="M5 8.8c3.8.2 9.6-.6 12.6-1.4" stroke="#f4f4f4" stroke-width="1.4" stroke-linecap="round" fill="none"/>
  <circle cx="21.6" cy="11.4" r="1.5" fill="#f4f4f4"/>
  <text class="sx-owl-z" x="21.6" y="5.6" font-size="6.5" font-weight="700" font-family="system-ui, sans-serif" fill="#6f95c4">z</text></svg>`;
const owlInner = (k: OwlKind) => k === 'owl' ? `<span class="sx-owl" role="img" title="${OWL_LABEL}" aria-label="${OWL_LABEL}">🦉</span>`
  : k === 'sleepy' ? `<span class="sx-owl sx-owl-sleepy" role="img" title="${SLEEPY_LABEL}" aria-label="${SLEEPY_LABEL}">${SLEEPY_OWL}</span>` : '';
/** which owl now ('' none): Little surprises On, and the time of day outside the strip (the person's own zone; during the
    owner's Preview a day the real time of day still counts) */
export const owlNow = (): OwlKind => surprisesOn() ? owlAt(surpriseWall()) : '';
/** the owl's slot beside RUNNING ('' when no timer runs) */
export const owlHtml = (t: Timer | undefined) => { if (!t) return ''; const k = owlNow(); return `<span class="sx-owl-slot" data-owl="${k}">${owlInner(k)}</span>`; };
/** keep every owl slot on screen up to date (cheap: nothing when none is on screen, or when nothing changed) */
export function tickOwls() {
  const slots = document.querySelectorAll<HTMLElement>('.sx-owl-slot');
  if (!slots.length) return;
  const k = owlNow();
  slots.forEach(el => { if (el.dataset.owl !== k) { el.dataset.owl = k; el.innerHTML = owlInner(k); } });
}

/* ---------- full-screen flip clock (fullscreen.ts; remembered per device) ---------- */
const FLIP_KEY = 'ot_flip_clock';
export const flipOn = () => surprisesOn() && get(FLIP_KEY) === '1';
export function toggleFlip(): boolean { const on = !flipOn(); put(FLIP_KEY, on ? '1' : null); return on; }

/* ---------- the keyboard code: 10 s of rainbow (no purple), then back to orange ---------- */
const konami = konamiDetector();
const DISCO = ['#e5484d', '#f39a3d', '#f5c84c', '#46b96a', '#2bb3a6', '#3d8fe0'];
let discoTimer: ReturnType<typeof setInterval> | undefined, discoEnd: ReturnType<typeof setTimeout> | undefined;
function onKey(e: KeyboardEvent) {
  if (!surprisesOn() || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return;
  const t = (e.target as HTMLElement | null), a = document.activeElement as HTMLElement | null;
  const field = (el: HTMLElement | null) => !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));
  if (field(t) || field(a)) return;   // never while typing in a field
  if (konami(e.key)) disco();
}
function disco() {
  stopDisco(false);
  const root = document.documentElement, bar = document.createElement('div');
  bar.className = 'sx-rainbow' + (reduced() ? ' still' : ''); bar.setAttribute('aria-hidden', 'true');
  document.body.appendChild(bar); root.classList.add('sx-disco');
  if (!reduced()) {
    let i = 0;
    discoTimer = setInterval(() => { const c = DISCO[i++ % DISCO.length]; root.style.setProperty('--accent', c); root.style.setProperty('--accent-2', c); }, 350);
  }
  discoEnd = setTimeout(() => stopDisco(true), 10000);
}
function stopDisco(say: boolean) {
  const root = document.documentElement, was = root.classList.contains('sx-disco');
  clearInterval(discoTimer); clearTimeout(discoEnd); discoTimer = discoEnd = undefined;
  root.style.removeProperty('--accent'); root.style.removeProperty('--accent-2'); root.classList.remove('sx-disco');
  document.querySelectorAll('.sx-rainbow').forEach(e => e.remove());
  if (say && was) toast('Back to orange. 🍂');
}

/* ---------- the owner's daily line (Settings, under the version) ---------- */
export function ownerLineHtml(): string {
  const line = surprisesOn() ? ownerLine(State.role, today()) : '';
  return line ? `<p class="sx-owner-line" id="ownerLine">${escapeHtml(line)}</p>` : '';
}
/** the Settings switch (everyone) */
export function surprisesCard(): string {
  const on = surprisesOn();
  return `<div class="card" id="surprisesCard">
    <h2>Little surprises</h2>
    <label class="h-switch" style="display:inline-flex; align-items:center; gap:10px; font-weight:600; cursor:pointer;">
      <input type="checkbox" class="switch" id="surprisesOn" ${on ? 'checked' : ''}><span id="surprisesOnLabel">${on ? 'On' : 'Off'}</span></label>
    <p class="muted-text" style="margin-top:8px;">Small seasonal touches on holidays and in December, and a few hidden extras. Saved on this device. Off: none of them appear.</p>
  </div>` + (on ? birthdaysCard() : '');   // Birthdays: only while Little surprises is On (Off: hidden, the saved list is kept)
}
export function wireSurprisesCard(rerender: () => void) {
  const sw = document.getElementById('surprisesOn') as HTMLInputElement | null;
  if (sw) sw.onchange = () => { setSurprisesOn(sw.checked); rerender(); };
  wireBirthdaysCard();
}

/* ---------- "Preview a day": the card (from the 7-tap card) and the pill while it is on ---------- */
function previewCardHtml(msg = ''): string {
  const p = activePreview(), real = realWall().slice(0, 10);
  return `<div class="sx-adv-head"><span>Preview a day</span><button type="button" class="sx-x" data-sx-close aria-label="Close">✕</button></div>
    <p class="sx-prev-help">See any day's Little surprises on this device, without changing its clock. Only the surprises see this date (with today's time of day); your time, timers, totals and reports stay on today's real date. It ends by itself at midnight, or when you tap Stop. With Little surprises Off (Settings), a preview shows nothing.</p>
    <input type="date" id="sxPrevDate" min="${PREVIEW_MIN}" max="${PREVIEW_MAX}" value="${p ? p.date : real}" aria-label="Day to preview">
    <div class="sx-prev-acts"><button type="button" class="btn primary" id="sxPrevGo">Preview</button><button type="button" class="btn" id="sxPrevStop"${p ? '' : ' disabled'}>Stop preview</button></div>
    <p class="sx-prev-now" id="sxPrevNow">${escapeHtml(msg || (p ? `Previewing ${previewLabel(p.date)}` : 'No preview: the surprises show today.'))}</p>`;
}
function openPreviewCard(msg = '') {
  if (State.role !== 'owner') return;
  const bg = openCard(previewCardHtml(msg), 'sx-preview', false);
  const go = bg.querySelector<HTMLButtonElement>('#sxPrevGo'), stop = bg.querySelector<HTMLButtonElement>('#sxPrevStop');
  if (go) go.onclick = ev => {
    ev.stopPropagation();
    const d = bg.querySelector<HTMLInputElement>('#sxPrevDate')?.value || '';
    if (!validDate(d)) { const n = $id('sxPrevNow'); if (n) n.textContent = 'Pick a date first.'; return; }
    startPreview(d);
  };
  if (stop) stop.onclick = ev => { ev.stopPropagation(); stopPreview(); };
}
function startPreview(date: string) {
  if (State.role !== 'owner' || !State.meId || !validDate(date)) return;
  const was = activePreview();
  put(PREVIEW_KEY, JSON.stringify({date, setOn: realWall().slice(0, 10), by: State.meId} satisfies Preview));
  if (was && was.date !== date) document.querySelectorAll('.sx-egg').forEach(e => e.remove());
  closeCard(); lastKey = ''; refreshSurprises();   // (the previewed day's effect plays, as on a new day)
}
export function stopPreview() {
  clearPreview();
  document.querySelectorAll('.sx-egg').forEach(e => e.remove());   // (a preview's egg goes with it)
  closeCard(); lastKey = ''; refreshSurprises();
}
/** the pill under the header while a preview is on: never forgotten ('' = none) */
function paintPreviewPill(date: string) {
  let pill = $id('sxPrevPill');
  if (!date) { pill?.remove(); return; }
  if (!pill) {
    pill = document.createElement('div'); pill.id = 'sxPrevPill'; pill.className = 'sx-prev-pill'; pill.setAttribute('role', 'status');
    const main = $id('main'); if (main?.parentNode) main.parentNode.insertBefore(pill, main); else document.body.prepend(pill);
  }
  pill.innerHTML = `<span>Previewing surprises for <b id="sxPrevPillDate">${escapeHtml(previewLabel(date))}</b></span> · <button type="button" class="sx-prev-stop" id="sxPrevPillStop">Stop</button>`;
  pill.querySelector<HTMLButtonElement>('#sxPrevPillStop')!.onclick = ev => { ev.stopPropagation(); stopPreview(); };
}

/* ---------- birthdays: the opening (once per load); the card on the first open of the day on each device ---------- */
const BD_CARD_KEY = () => surpriseStoreKey('ot_bd_card_' + (State.meId || 'anon'));   // the day the card was shown here (a preview: its own store)
let cardWait: ReturnType<typeof setTimeout> | undefined;
function openingBirthday(date: string) {
  if (!todaysBd.own) { birthdayFx(false); return; }          // family & friends: a few balloons
  if (get(BD_CARD_KEY()) === date) { birthdayFx(true); return; }   // the card was already shown today on this device: the party only
  showCardWhenQuiet(date, true);
}
/** the card at the first quiet moment: never while someone types, never over a window, a pick-list, another card, the
    welcome or the full-screen timer (it waits until that has closed); party = with the confetti and balloons */
function showCardWhenQuiet(date: string, party: boolean) {
  clearTimeout(cardWait);
  const tryNow = () => {
    if (!surprisesOn() || !todaysBd.own || today() !== date || birthdayCardOpen()) return;
    if (document.visibilityState === 'visible' && openBirthdayCard(ownText(firstName(me()?.name)))) {
      put(BD_CARD_KEY(), date);
      if (party) birthdayFx(true);
      return;
    }
    cardWait = setTimeout(tryNow, 1000);
  };
  tryNow();
}
/** the 🎂 Birthday! pill (all day on the person's own birthday): opens the card again */
function onBdayPill(ev: Event) { ev.stopPropagation(); if (surprisesOn() && todaysBd.own) showCardWhenQuiet(today(), false); }

/* ---------- Advent calendar: today's door, and every earlier one (per person, per device) ---------- */
const ADV_KEY = (y: number) => surpriseStoreKey('ot_advent_' + (State.meId || 'anon') + '_' + y);   // (a preview: its own store)
function openedDoors(y: number): number[] { try { const v = JSON.parse(get(ADV_KEY(y)) || '[]'); return Array.isArray(v) ? v.filter(n => typeof n === 'number') : []; } catch { return []; } }
function markOpened(y: number, n: number) { const o = openedDoors(y); if (!o.includes(n)) put(ADV_KEY(y), JSON.stringify([...o, n])); }
const KIND_LABEL: Record<string, string> = {trivia: 'Did you know?', carol: '🎶 A carol', winter: '❄️ Winter fact', work: '💼 For the work days', anim: ''};
const ANIM: Record<string, string> = {
  snowman: `<svg viewBox="0 0 80 96" class="sx-anim sx-snowman" aria-hidden="true"><circle class="b1" cx="40" cy="74" r="20" fill="#f4f6fb" stroke="#c9d3e3"/><circle class="b2" cx="40" cy="44" r="14" fill="#f4f6fb" stroke="#c9d3e3"/>
    <g class="b3"><circle cx="40" cy="22" r="10" fill="#f4f6fb" stroke="#c9d3e3"/><circle cx="36.5" cy="20" r="1.4" fill="#2a2017"/><circle cx="43.5" cy="20" r="1.4" fill="#2a2017"/><path d="M40 23l9 2-9 1.5z" fill="#f39a3d"/>
    <rect x="31" y="4" width="18" height="7" rx="1" fill="#2a2017"/><rect x="28" y="10" width="24" height="3" rx="1" fill="#2a2017"/><path d="M30 30q10 6 20 0" stroke="#d43a3a" stroke-width="4" fill="none"/></g>
    <circle cx="40" cy="40" r="1.6" fill="#2a2017"/><circle cx="40" cy="47" r="1.6" fill="#2a2017"/></svg>`,
  gingerbread: `<svg viewBox="0 0 80 96" class="sx-anim sx-ginger" aria-hidden="true"><g fill="#b8743d"><circle cx="40" cy="20" r="12"/><rect x="28" y="30" width="24" height="34" rx="10"/>
    <rect x="12" y="34" width="20" height="9" rx="4.5"/><rect class="arm" x="48" y="34" width="20" height="9" rx="4.5"/><rect x="27" y="58" width="10" height="28" rx="5"/><rect x="43" y="58" width="10" height="28" rx="5"/></g>
    <g fill="#fff"><circle cx="36" cy="18" r="1.6"/><circle cx="44" cy="18" r="1.6"/><circle cx="40" cy="42" r="1.8"/><circle cx="40" cy="50" r="1.8"/></g><path d="M35 24q5 4 10 0" stroke="#fff" stroke-width="1.6" fill="none"/></svg>`,
  candycane: `<svg viewBox="0 0 80 96" class="sx-anim sx-cane" aria-hidden="true"><g class="spin"><path d="M48 86V30a12 12 0 0 0-24 0" stroke="#fff" stroke-width="9" fill="none" stroke-linecap="round"/>
    <path d="M48 86V30a12 12 0 0 0-24 0" stroke="#d43a3a" stroke-width="9" fill="none" stroke-linecap="round" stroke-dasharray="6 6"/></g></svg>`,
  candy: `<svg viewBox="0 0 80 96" class="sx-anim sx-candy" aria-hidden="true"><g class="drop"><circle cx="32" cy="22" r="5" fill="#e8607a"/><circle cx="44" cy="16" r="5" fill="#3fbf9b"/><circle cx="52" cy="26" r="5" fill="#f5c84c"/></g>
    <g transform="translate(14 30) scale(2.4)">${STOCKING.replace(/<\/?svg[^>]*>/g, '')}</g></svg>`,
};
function doorBody(n: number, y: number): string {
  if (n === 25) return `<div class="sx-star" aria-hidden="true">⭐</div><h3 class="sx-xmas">Merry Christmas!</h3><p>Thank you for a wonderful year. Enjoy the day.</p>`;
  const d = DOORS[n - 1]; if (!d) return '';
  return (d.anim ? ANIM[d.anim] : `<div class="sx-kind">${KIND_LABEL[d.kind]}</div>`) + `<p class="sx-door-text">${escapeHtml(doorText(n, y))}</p>`;
}
function adventHtml(show: number, date: string): string {
  const y = +date.slice(0, 4), opened = openedDoors(y), xmas = isChristmasDay(date);
  const cell = (n: number) => {
    const ok = doorOpenable(n, date), cls = ['sx-cell', ok ? '' : 'locked', opened.includes(n) ? 'opened' : '', n === show ? 'cur' : '', n === adventDay(date) ? 'today' : ''].filter(Boolean).join(' ');
    return `<button type="button" class="${cls}" data-door="${n}" ${ok ? '' : 'disabled aria-disabled="true"'} aria-label="Door ${n}${ok ? '' : ' (locked)'}">${n}${ok ? '' : '<span class="sx-lock">🔒</span>'}</button>`;
  };
  return `<div class="sx-adv-head"><span>${show === 25 ? '⭐ Christmas Day' : `🎁 Door ${show} · December ${show}`}</span><button type="button" class="sx-x" data-sx-close aria-label="Close">✕</button></div>
    <div class="sx-door-body" id="sxDoorBody">${doorBody(show, y)}</div>
    <div class="sx-grid" id="sxGrid">${Array.from({length: 24}, (_, i) => cell(i + 1)).join('')}
      <button type="button" class="sx-cell sx-cell-star ${xmas ? '' : 'locked'} ${show === 25 ? 'cur' : ''}" data-door="25" ${xmas ? '' : 'disabled aria-disabled="true"'} aria-label="${xmas ? 'Merry Christmas!' : 'Christmas (locked)'}">${xmas ? '⭐ Merry Christmas!' : '25 <span class="sx-lock">🔒</span>'}</button></div>`;
}
function openAdvent(n: number) {
  const date = today(), y = +date.slice(0, 4);
  if (!surprisesOn() || !calendarDays(date) || (n === 25 ? !isChristmasDay(date) : !doorOpenable(n, date))) return;
  markOpened(y, n);
  const cur = $id('sxCard')?.querySelector('.sx-advent');   // already open: change the door in place (no new fade-in)
  let bg: HTMLElement;
  if (cur) { cur.innerHTML = adventHtml(n, date); bg = $id('sxCard')!; } else bg = openCard(adventHtml(n, date), 'sx-advent', false);
  bg.querySelectorAll<HTMLButtonElement>('[data-door]').forEach(b => b.onclick = ev => { ev.stopPropagation(); if (!b.disabled) openAdvent(+b.dataset.door!); });
  if (n === 25 && !reduced()) { const star = bg.querySelector<HTMLElement>('.sx-star'); if (star) burst(star, 12, ['⭐', '✦', '★'], 'sx-burst big', true); }
  lastKey = ''; refreshSurprises();   // the door chip shows it opened
}
function onDoorTap(ev: Event) {
  ev.stopPropagation();
  const date = today();
  openAdvent(isChristmasDay(date) ? 25 : adventDay(date));
}

/** start (main.ts, once) */
export function installSurprises() {
  $id('brandTap')?.addEventListener('click', onBrandTap);
  $id('sxDoor')?.addEventListener('click', onDoorTap);
  $id('sxBdayPill')?.addEventListener('click', onBdayPill);
  bdHooks.changed = () => { lastKey = ''; refreshSurprises(); refreshCard(); };   // the birthday list changed (here, another device, a merge)
  document.addEventListener('keydown', onKey);
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && $id('sxCard')) closeCard(); });
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshSurprises(); });
  setInterval(refreshSurprises, 60_000);   // a new day (or Christmas Eve's 6 PM) while the app stays open
  put('ot_sx_fx', null); put('ot_sx_sleigh', null);   // retired: the "played today" marks of the once-a-day rule (before 2026-10-31)
  scheduleRandomFx();
  holdTimer = setTimeout(surprisesReady, 2000);   // (no copy on this device and no signal: the decorations show anyway; the effect after 2 s)
  refreshSurprises();
}
